import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useProposeImmediateMeeting } from "@/hooks/useImmediateMeetings";
import { Loader2, Video } from "lucide-react";

interface SuggestMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentorshipRequestId: string;
  threadId: string;
  entrepreneurName: string;
}

export function SuggestMeetingDialog({
  open,
  onOpenChange,
  mentorshipRequestId,
  threadId,
  entrepreneurName,
}: SuggestMeetingDialogProps) {
  const [duration, setDuration] = useState<string>("30");
  const [note, setNote] = useState("");
  const [availability, setAvailability] = useState("");
  const proposeMeeting = useProposeImmediateMeeting();

  const handleSubmit = async () => {
    await proposeMeeting.mutateAsync({
      mentorshipRequestId,
      threadId,
      duration: parseInt(duration),
      note: note.trim() || undefined,
      availability: availability.trim() || undefined,
    });
    onOpenChange(false);
    setNote("");
    setAvailability("");
    setDuration("30");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Suggest a Meeting
          </DialogTitle>
          <DialogDescription>
            Propose a Zoom meeting with {entrepreneurName}. They will be notified and can accept or continue asynchronously.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Meeting Duration</Label>
            <RadioGroup value={duration} onValueChange={setDuration} className="flex gap-4">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="15" id="d15" />
                <Label htmlFor="d15" className="font-normal cursor-pointer">15 min</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="30" id="d30" />
                <Label htmlFor="d30" className="font-normal cursor-pointer">30 min</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="45" id="d45" />
                <Label htmlFor="d45" className="font-normal cursor-pointer">45 min</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="availability">Your Availability (Optional)</Label>
            <Textarea
              id="availability"
              placeholder="e.g., 'I'm free Tuesday or Thursday afternoons next week'"
              value={availability}
              onChange={(e) => setAvailability(e.target.value)}
              className="resize-none"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note to Entrepreneur (Optional)</Label>
            <Textarea
              id="note"
              placeholder="Add any context about what you'd like to discuss..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>

          <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
            <p>
              <strong>Note:</strong> The Zoom meeting will only be created after {entrepreneurName} accepts your proposal.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={proposeMeeting.isPending}>
            {proposeMeeting.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Video className="h-4 w-4 mr-2" />
            )}
            Send Proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
